import * as React from 'react';
import styled from 'styled-components';
import {useTicketUpdate} from '../hooks/useTicketUpdate';
import {useDeleteTicket} from '../hooks/useDeleteTicket';
import {useNavigate} from '../../../router/compat';
import {TicketDetailHeader} from './TicketDetailHeader';
import {TicketTabs} from './TicketTabs';
import {TicketMetaPanel} from './TicketMetaPanel';
import {OverviewTab} from './OverviewTab';
import {ActivityTab} from './ActivityTab';
import {StreamTab} from './StreamTab';
import {AiSummaryBlock} from './AiSummaryBlock';
import {WatchersList} from './WatchersList';
import {RelatedTab} from './RelatedTab';
import {AddLinkModal} from './AddLinkModal';
import {LinkedTasksTab} from './LinkedTasksTab';
import {TimeTab} from './TimeTab';
import {AddForecastTaskModal} from './AddForecastTaskModal';
import {TicketAttachmentsTab} from './TicketAttachmentsTab';
import type {TicketAttachment} from './TicketAttachmentsTab';
import {DeleteTicketModal} from './DeleteTicketModal';
import {DeleteAttachmentModal} from './DeleteAttachmentModal';
import {useAttachmentUpload} from '../hooks/useAttachmentUpload';
import {useDeleteAttachment} from '../hooks/useDeleteAttachment';
import {useOpenNativeTaskModal} from '../hooks/useOpenNativeTaskModal';
import {useAuth} from '../../auth/context/AuthContext';
import {canDeleteAttachment, hasTicketPerm, TICKET_PERMS} from '../../auth/utils/ticketPerms';
import type {useTicketDetailQuery$data} from '../hooks/__generated__/useTicketDetailQuery.graphql';
import type {Ticket} from '../types/ticket';

/** Wraps the loading, not-found and error states, which are narrower than the page. */
export const MainNarrow = styled.main`
	margin: 0 auto;
	max-width: 64rem;
	padding: 32px 24px;
`;
const MainWide = styled.main`
	margin: 0 auto;
	max-width: 96rem;
	padding: 24px;
`;
const ErrorBox = styled.div`
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	border: ${p => p.theme.sys.size.borderwidth.thin} solid ${p => p.theme.sys.color.border.error};
	background: ${p => p.theme.sys.color.background.neutral['2']};
	padding: ${p => p.theme.sys.spacing[400]};
`;
const ErrorAlert = styled(ErrorBox)`
	margin-bottom: ${p => p.theme.sys.spacing[400]};
`;
const ErrorText = styled.p`
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => p.theme.sys.color.text.semantic.error};
	margin: 0;
`;
const DetailGrid = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	gap: 24px;
	/* Side-by-side from 1280px up. That is the narrowest viewport where the Details
	   panel can take its minimum 20rem and still leave the content column enough room
	   to be read once Forecast's own left navigation has taken its share. Below it the
	   panel stacks under the content rather than shrinking the content column toward
	   zero, which is what the fixed-width panel used to do.

	   The panel is a range, not a fixed width: it takes up to 22rem so a wide monitor
	   spends its extra space on the panel's label/value pairs instead of stranding them
	   in white space, and the content column keeps the rest. */
	@media (min-width: 1280px) {
		grid-template-columns: minmax(0, 1fr) minmax(20rem, 22rem);
	}
`;
const MainColumn = styled.section`
	min-width: 0;
	/* Tab bodies that are prose rather than tables — Overview, Activity, Stream — carry
	   pasted URLs and email quotes with no break opportunity in them. Without this the
	   grid track is bounded but the text is not, so the line runs on under the Details
	   panel. Tables handle the same problem with truncation and their own scroll box. */
	overflow-wrap: break-word;
	> * + * {
		margin-top: 20px;
	}
`;
const SideColumn = styled.div`
	min-width: 0;
	> * + * {
		margin-top: 16px;
	}
`;
const AiBlock = styled.div`
	margin-bottom: ${p => p.theme.sys.spacing[400]};
`;

interface ErrorBoundaryState {
	error: Error | null;
}

/**
 * Catches a render throw from the detail page and shows the message in place.
 *
 * Mount it with the fetch key as `key` so a refetch remounts it — otherwise a
 * boundary that has already caught stays stuck on the old error.
 */
export class RouteErrorBoundary extends React.Component<{children: React.ReactNode}, ErrorBoundaryState> {
	override state: ErrorBoundaryState = {error: null};
	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return {error};
	}
	override render() {
		if (this.state.error) {
			return (
				<MainNarrow>
					<ErrorBox role="alert">
						<ErrorText>Error: {this.state.error.message}</ErrorText>
					</ErrorBox>
				</MainNarrow>
			);
		}
		return this.props.children;
	}
}

// Both detail queries — by id and by slug — select the same fields and spread the same
// fragments, so one set of types describes either one's payload.
type TicketDetailData = NonNullable<NonNullable<useTicketDetailQuery$data['viewer']>['ticketDetail']>;
export type TicketRef = TicketDetailData['ticket'];
export type Timeline = TicketDetailData['timeline'];
export type Attachments = TicketDetailData['attachments'];

/**
 * Shapes the Relay ticket into the REST shape `useTicketUpdate` still writes through.
 *
 * `labels`, `sla` and the project fields are left empty: the panels that show them read
 * the Relay fragments directly, and nothing in the update path looks at any of them.
 */
function toRestTicket(t: TicketRef): Ticket {
	return {
		id: t.id,
		globalId: t.id,
		slug: t.slug,
		account_id: '',
		requester_id: t.requester?.id ?? '',
		assignee_id: t.assigneeId ?? null,
		subject: t.subject,
		description: t.description ?? null,
		status: t.status as Ticket['status'],
		priority: t.priority as Ticket['priority'],
		channel: (t.channel ?? 'manual') as Ticket['channel'],
		created_at: t.createdAt as string,
		updated_at: t.updatedAt as string,
		closed_at: (t.closedAt as string | null) ?? null,
		last_customer_message_at: null,
		requester_name: t.requester?.name ?? null,
		requester_email: t.requester?.email ?? null,
		assignee_name: t.assignee?.fullName ?? null,
		labels: [],
		sla: null,
		project_name: null,
		has_project_link: false,
	};
}

function toTabAttachments(attachments: Attachments) {
	return attachments.map(a => ({
		id: a.id,
		filename: a.filename,
		contentType: a.contentType,
		sizeBytes: a.sizeBytes,
		downloadUrl: a.downloadUrl,
		createdAt: a.createdAt as string,
		// Prefer the resolved Forecast Person's name; fall back to the requester name
		// for email attachments that have no Forecast person behind them.
		uploadedByName: a.uploadedBy?.fullName ?? a.uploadedByName ?? null,
		// The own-vs-others delete gate compares this against the viewer.
		// Both are Person GLOBAL ids (viewer.actualPersonId is toGlobalId'd), so they
		// are directly comparable. Null for an inbound email attachment with no
		// Forecast person behind it.
		uploadedByPersonId: a.uploadedBy?.id ?? null,
	}));
}

interface Props {
	ticketRef: TicketRef;
	timeline: Timeline;
	attachments: Attachments;
	/** Re-runs the owning route's detail query after a write. */
	refetch: () => void;
	/** The viewer's person id, so "you" can be marked and time logged against them. */
	currentPersonId: string;
}

/**
 * The whole ticket detail page below the query boundary.
 *
 * Shared by both detail routes. A ticket is reachable by slug (`ACME-1042`) and by raw
 * id, which the two routes fetch with different queries — but the page they render is
 * the same page, so it is built once here. Anything route-shaped (the query, its
 * variables, the fetch key) stays in the route; everything the reader sees lives here.
 */
export function TicketDetailContent({ticketRef, timeline, attachments, refetch, currentPersonId}: Props) {
	const [activeTab, setActiveTab] = React.useState('overview');
	const [addLinkOpen, setAddLinkOpen] = React.useState(false);
	const [addTaskOpen, setAddTaskOpen] = React.useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
	const [attachmentToDelete, setAttachmentToDelete] = React.useState<{id: string; filename: string} | null>(null);
	const navigate = useNavigate();
	const openNativeTaskModal = useOpenNativeTaskModal();
	const {user} = useAuth();
	const canDelete = !!user && hasTicketPerm(user.ticketPerms, TICKET_PERMS.MANAGE);
	const {ticket, saving, pending, error, updateField} = useTicketUpdate(toRestTicket(ticketRef), refetch);
	// Every save disables Resolve, but only a write to resolved should read as "Resolving…".
	const resolving = pending?.field === 'status' && pending.value === 'resolved';
	const {submit: deleteTicket, loading: deleting} = useDeleteTicket();
	const {submit: deleteAttachment, loading: deletingAttachment} = useDeleteAttachment();
	const attachmentUpload = useAttachmentUpload();

	const handleDeleteConfirm = React.useCallback(() => {
		deleteTicket(ticketRef.id).then(
			() => navigate('/tickets'),
			() => setDeleteModalOpen(false)
		);
	}, [deleteTicket, ticketRef.id, navigate]);

	const handleUploadFile = React.useCallback(
		async (file: File) => {
			await attachmentUpload.upload(ticketRef.id, file);
			refetch();
		},
		[attachmentUpload, ticketRef.id, refetch]
	);

	// The row gate is advisory — the gateway re-derives ownership and
	// re-checks ATTACHMENT_DELETE_ALL, so a bypassed UI still gets a 403.
	const canDeleteThisAttachment = React.useCallback(
		(a: TicketAttachment) => canDeleteAttachment(user?.ticketPerms ?? [], currentPersonId, a.uploadedByPersonId),
		[user, currentPersonId]
	);

	const handleDeleteAttachmentConfirm = React.useCallback(() => {
		if (!attachmentToDelete) return;
		// Close on either outcome: on success the list is refetched without the row,
		// and on failure Util.CommitMutation has already surfaced the error modal.
		deleteAttachment(attachmentToDelete.id).then(
			() => {
				setAttachmentToDelete(null);
				refetch();
			},
			() => setAttachmentToDelete(null)
		);
	}, [deleteAttachment, attachmentToDelete, refetch]);

	const handleResolve = React.useCallback(() => {
		updateField('status', 'resolved');
	}, [updateField]);

	// The Related tab only renders related/child/project links; forecast_task links live in the Tasks tab.
	// Keep the badge in sync with what the tab actually shows.
	const linkCount = ticketRef.links?.filter(l => l.linkType !== 'forecast_task').length ?? 0;
	// Count only time logged through this ticket, matching what the Time tab lists.
	const timeEntryCount = ticketRef.ticketTimeRegistrations?.length ?? 0;

	const existingLinkTargetIds = (ticketRef.links ?? [])
		.map(l => l.targetTicket?.id ?? l.targetProject?.id)
		.filter((id): id is string => id != null);

	const tabAttachments = React.useMemo(() => toTabAttachments(attachments), [attachments]);

	// Counts ride inside the tab label. A zero is left off rather than rendered —
	// an empty tab should not advertise its emptiness.
	const tabs = [
		{id: 'overview', label: 'Overview'},
		{id: 'activity', label: 'Activity'},
		{id: 'stream', label: 'Stream'},
		{id: 'attachments', label: 'Attachments', count: attachments.length || null},
		{id: 'time', label: 'Time', count: timeEntryCount || null},
		{id: 'tasks', label: 'Tasks', count: ticketRef.linkedTasks?.length || null},
		{id: 'related', label: 'Related', count: linkCount || null},
	];

	return (
		<MainWide>
			<TicketDetailHeader
				ticket={ticketRef}
				onResolve={handleResolve}
				onDelete={canDelete ? () => setDeleteModalOpen(true) : undefined}
				saving={saving}
				resolving={resolving}
			/>
			{/* The AI reading belongs to the ticket, not to one tab, so it sits above the
			    tab bar: switching tabs never hides what the model concluded. */}
			<AiBlock>
				<AiSummaryBlock ticketRef={ticketRef} />
			</AiBlock>
			{error && (
				<ErrorAlert role="alert">
					<ErrorText>{error}</ErrorText>
				</ErrorAlert>
			)}
			<TicketTabs activeTab={activeTab} onTabChange={setActiveTab} tabs={tabs}>
				<DetailGrid data-testid="ticket-detail-grid">
					<MainColumn data-testid="ticket-detail-main">
						{activeTab === 'related' ? (
							<RelatedTab
								ticket={ticketRef}
								requesterName={ticket.requester_name ?? ''}
								onAddLink={() => setAddLinkOpen(true)}
								onRefetch={refetch}
							/>
						) : activeTab === 'tasks' ? (
							<LinkedTasksTab
								ticket={ticketRef}
								onAddTask={() => setAddTaskOpen(true)}
								currentPersonId={currentPersonId}
								onRefetch={refetch}
							/>
						) : activeTab === 'time' ? (
							<TimeTab ticket={ticketRef} currentPersonId={currentPersonId} onRefetch={refetch} />
						) : activeTab === 'attachments' ? (
							<TicketAttachmentsTab
								attachments={tabAttachments}
								onUploadFile={handleUploadFile}
								onDeleteAttachment={(id, filename) => setAttachmentToDelete({id, filename})}
								canDeleteAttachment={canDeleteThisAttachment}
							/>
						) : activeTab === 'activity' ? (
							<ActivityTab entries={timeline} requesterEmail={ticket.requester_email ?? ''} />
						) : activeTab === 'stream' ? (
							<StreamTab
								entries={timeline}
								ticketId={ticket.id}
								requesterEmail={ticket.requester_email ?? ''}
								requesterName={ticket.requester_name ?? ''}
								onRefetch={refetch}
							/>
						) : (
							<OverviewTab
								description={ticket.description}
								timeline={timeline}
								requesterEmail={ticket.requester_email ?? ''}
								onNavigateToActivity={() => setActiveTab('activity')}
							/>
						)}
					</MainColumn>
					<SideColumn>
						<TicketMetaPanel
							ticket={ticketRef}
							saving={saving}
							onUpdateField={updateField as (field: string, value: unknown) => void}
							onNavigateToRelated={() => setActiveTab('related')}
						/>
						<WatchersList ticket={ticketRef} currentPersonId={currentPersonId} />
					</SideColumn>
				</DetailGrid>
			</TicketTabs>
			<AddLinkModal
				ticketId={ticketRef.id}
				existingLinkTargetIds={existingLinkTargetIds}
				hasProjectLink={(ticketRef.links ?? []).some(l => l.linkType === 'project')}
				open={addLinkOpen}
				onClose={() => setAddLinkOpen(false)}
				onSuccess={refetch}
			/>
			<AddForecastTaskModal
				ticketId={ticketRef.id}
				open={addTaskOpen}
				onClose={() => setAddTaskOpen(false)}
				onSuccess={refetch}
				onTaskCreated={openNativeTaskModal}
			/>
			<DeleteTicketModal
				open={deleteModalOpen}
				ticketSlug={ticketRef.slug}
				ticketSubject={ticketRef.subject}
				loading={deleting}
				onConfirm={handleDeleteConfirm}
				onCancel={() => setDeleteModalOpen(false)}
			/>
			<DeleteAttachmentModal
				open={attachmentToDelete !== null}
				filename={attachmentToDelete?.filename ?? ''}
				loading={deletingAttachment}
				onConfirm={handleDeleteAttachmentConfirm}
				onCancel={() => setAttachmentToDelete(null)}
			/>
		</MainWide>
	);
}
